import { useState, useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import ArticleCard from "../components/ArticleCard";
import { Button } from "../components/ui/button";
import { Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import SEO from "../components/SEO";

const ArticlesPage = () => {
  const { slug: categorySlug } = useParams();
  const [searchParams] = useSearchParams();
  const [articles, setArticles] = useState([]);
  const [categories, setCategories] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCategory, setSelectedCategory] = useState(categorySlug || null);
  const articlesPerPage = 9;

  useEffect(() => {
    setSelectedCategory(categorySlug || null);
    setCurrentPage(1);
  }, [categorySlug]);

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    fetchArticles();
  }, [currentPage, selectedCategory]);

  const fetchCategories = async () => {
    try {
      const response = await axios.get(`${API}/categories`);
      setCategories(response.data);
    } catch (error) {
      console.error("Failed to fetch categories:", error);
    }
  };

  const fetchArticles = async () => {
    setLoading(true);
    try {
      let url = `${API}/articles?limit=${articlesPerPage}&skip=${(currentPage - 1) * articlesPerPage}`;
      
      if (selectedCategory === "tumblr-archive") {
        // Archive page: query by is_archive flag (articles are also in real categories)
        url += `&archive=true`;
      } else if (selectedCategory) {
        // Find category by slug
        const category = categories.find(c => c.slug === selectedCategory);
        if (category) {
          url += `&category=${category.category_id}`;
        }
      }
      
      const response = await axios.get(url);
      setArticles(response.data.articles);
      setTotal(response.data.total);
    } catch (error) {
      console.error("Failed to fetch articles:", error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(total / articlesPerPage);
  const currentCategory = categories.find(c => c.slug === selectedCategory);

  return (
    <div className="pt-16 min-h-screen" data-testid="articles-page">
      <SEO 
        title={currentCategory ? currentCategory.name : "Articles"}
        description={currentCategory ? `${currentCategory.name} articles from Mancunia Rebél - empowering independent creatives.` : "All articles from Mancunia Rebél covering music, fashion, arts, film, technology and more."}
        url={currentCategory ? `/category/${currentCategory.slug}` : "/articles"}
        keywords={currentCategory ? [currentCategory.name.toLowerCase()] : ["articles", "magazine"]}
      />
      <section className="py-12 md:py-16 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <span className="font-mono text-xs uppercase tracking-widest text-primary">
            {currentCategory ? currentCategory.name : "All Articles"}
          </span>
          <h1 className="font-heading font-black text-5xl md:text-7xl uppercase tracking-tighter mt-2">
            {currentCategory ? currentCategory.name : "Articles"}
          </h1>
          {currentCategory && (
            <p className="text-muted-foreground mt-4 max-w-2xl">
              {currentCategory.description}
            </p>
          )}
        </div>
      </section>

      {/* Category Filter */}
      <section className="py-6 border-b border-border">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="flex flex-wrap gap-2">
            <Button
              variant={!selectedCategory ? "default" : "outline"}
              onClick={() => setSelectedCategory(null)}
              className={`h-10 px-6 font-mono uppercase tracking-widest text-xs ${
                !selectedCategory 
                  ? "bg-primary text-primary-foreground" 
                  : "bg-transparent border border-border text-foreground hover:bg-white hover:text-black"
              }`}
              data-testid="filter-all"
            >
              All
            </Button>
            {categories.map((category) => (
              <Button
                key={category.category_id}
                variant={selectedCategory === category.slug ? "default" : "outline"}
                onClick={() => setSelectedCategory(category.slug)}
                className={`h-10 px-6 font-mono uppercase tracking-widest text-xs ${
                  selectedCategory === category.slug
                    ? "bg-primary text-primary-foreground"
                    : "bg-transparent border border-border text-foreground hover:bg-white hover:text-black"
                }`}
                data-testid={`filter-${category.slug}`}
              >
                {category.name}
              </Button>
            ))}
          </div>
        </div>
      </section>

      {/* Articles Grid */}
      <section className="py-16 md:py-24">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          {loading ? (
            <div className="flex justify-center py-24">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : articles.length === 0 ? (
            <div className="text-center py-24">
              <p className="text-muted-foreground text-lg">No articles found.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16">
                {articles.map((article) => (
                  <ArticleCard key={article.article_id} article={article} />
                ))}
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-center gap-4 mt-16">
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-4 font-mono text-xs"
                    data-testid="pagination-prev"
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    Prev
                  </Button>
                  <span className="font-mono text-sm text-muted-foreground">
                    Page {currentPage} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="bg-transparent border border-border text-foreground hover:bg-white hover:text-black h-10 px-4 font-mono text-xs"
                    data-testid="pagination-next"
                  >
                    Next
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default ArticlesPage;
